import React from 'react';
import { Col } from '../../lib/utils';
import { FeedListType, Feed } from '../../scenes/Feed/FeedPage';
import dayjs from 'dayjs';
import { getFeedBadge, getFeedScheduleTitle, getFeedSource } from '../../lib/feedSource';

const getTypeBadge = (feed: Feed) => {
    if (feed.postType === 'SCHEDULEREPORT' || feed.postType === 'GROUP_SESSION_LOG') {
        const badge = getFeedBadge(feed);
        return { label: badge.label, color: badge.color, bg: badge.background };
    }
    const postType = feed.postType;
    if (postType === 'FIXED') {
        return { label: '공지', color: '#fff', bg: '#F59E0B' };
    }
    return { label: '게시물', color: '#fff', bg: '#6B7280' };
};

type Props = {
    feedList: FeedListType;
    onClickDetail: (object: any, index: number) => void;
    setTarget: any;
    selectedIndex?: number;
};

const FeedTableView = ({ feedList, onClickDetail, setTarget, selectedIndex }: Props) => {
    return (
        <Col style={{ width: '100%', maxWidth: 786, overflowX: 'auto' }}>
            {/* 테이블 헤더 */}
            <div className="flex min-w-[786px] items-center bg-[#F1F5F9] rounded-t-[12px] px-[12px] py-[10px] border border-[#E2E8F0]">
                <span className="text-[12px] font-semibold text-[#64748B] w-[68px]">유형</span>
                <span className="text-[12px] font-semibold text-[#64748B] w-[80px]">대상</span>
                <span className="text-[12px] font-semibold text-[#64748B] w-[92px]">수업 시간</span>
                <span className="text-[12px] font-semibold text-[#64748B] w-[92px]">회차/유형</span>
                <span className="text-[12px] font-semibold text-[#64748B] flex-1">내용</span>
                <span className="text-[12px] font-semibold text-[#64748B] w-[72px]">작성자</span>
                <span className="text-[12px] font-semibold text-[#64748B] w-[64px]">작성일자</span>
                <span className="w-[28px]" />
            </div>

            {/* 테이블 바디 */}
            {feedList && feedList.length > 0 ? (
                feedList.map((item: Feed, index: number) => {
                    const isSchedule = item.postType === 'SCHEDULEREPORT';
                    const isNewOpportunity = getFeedSource(item) === 'newOpportunity';
                    const scheduleInfo = item.scheduleReport?.scheduleInfo;
                    const textContent = item.contents?.find((c) => c.contentType === 'TEXT')?.content || '';
                    const imageCount = item.contents?.filter((c) => c.contentType === 'IMG')?.length || 0;
                    const videoCount = item.contents?.filter((c) => c.contentType === 'VIDEO')?.length || 0;
                    const receivers = item.receivers || [];
                    let memberName = '-';
                    if (isSchedule) {
                        // 수업일지: 대상 회원
                        memberName = receivers[0]?.receiverName || '-';
                    } else if (item.role === 'member' && receivers.length > 0) {
                        // 회원 게시물: 대상 강사
                        memberName = receivers[0]?.receiverName || '-';
                    } else if (item.role === 'coach' && receivers.length > 0) {
                        // 강사 게시물: 대상 회원 (n명)
                        memberName = receivers.length === 1
                            ? receivers[0]?.receiverName
                            : `${receivers[0]?.receiverName} 외 ${receivers.length - 1}명`;
                    }
                    const badge = getTypeBadge(item);
                    const isSelected = selectedIndex === index;
                    return (
                        <div
                            key={item._id || index}
                            className={`flex min-w-[786px] items-center px-[12px] py-[11px] border-x border-b border-[#E2E8F0] cursor-pointer transition-colors ${
                                isSelected ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'
                            }`}
                            onClick={() => onClickDetail(item, index)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                onClickDetail(item, index);
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`${badge.label} ${memberName} 피드 상세 보기`}
                            aria-current={isSelected ? 'true' : undefined}
                        >
                            {/* 유형 배지 */}
                            <span className="w-[68px]">
                                <span
                                    className="inline-flex items-center px-[8px] py-[3px] rounded-[4px] text-[11px] font-semibold"
                                    style={{ backgroundColor: badge.bg, color: badge.color }}
                                >
                                    {badge.label}
                                </span>
                            </span>

                            {/* 회원 이름 */}
                            <span className="text-[13px] text-[#374151] w-[80px] truncate">
                                {memberName}
                            </span>

                            {/* 수업 시간 */}
                            <span className="w-[92px]">
                                {isSchedule && scheduleInfo?.startTime ? (
                                    <span className="flex flex-col leading-[15px]">
                                        <span className="text-[12px] font-medium text-[#374151]">
                                            {dayjs(scheduleInfo.startTime).format('M.DD(ddd)')}
                                        </span>
                                        <span className="text-[11px] text-[#9CA3AF]">
                                            {dayjs(scheduleInfo.startTime).format('HH:mm')}
                                            {scheduleInfo.endTime && ` - ${dayjs(scheduleInfo.endTime).format('HH:mm')}`}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="text-[12px] text-[#D1D5DB]">-</span>
                                )}
                            </span>

                            {/* PT 회차 / 신규기회 유형 */}
                            <span className="w-[92px] min-w-0">
                                {isSchedule && scheduleInfo ? (
                                    <span
                                        className="block max-w-[84px] truncate bg-[#E4EDFC] text-[#3B82F6] px-[5px] py-[1px] rounded-[3px] text-[11px] font-medium"
                                        title={
                                            isNewOpportunity
                                                ? getFeedScheduleTitle(item)
                                                : `${scheduleInfo.currentSession || '?'}/${scheduleInfo.totalSession || '?'}`
                                        }
                                    >
                                        {isNewOpportunity
                                            ? getFeedScheduleTitle(item)
                                            : `${scheduleInfo.currentSession || '?'}/${scheduleInfo.totalSession || '?'}`}
                                    </span>
                                ) : (
                                    <span className="text-[12px] text-[#D1D5DB]">-</span>
                                )}
                            </span>

                            {/* 내용 미리보기 */}
                            <span className="flex items-center flex-1 min-w-0 gap-[5px]">
                                <span className="text-[13px] text-[#374151] truncate">
                                    {textContent.slice(0, 40) || '(내용 없음)'}
                                </span>
                                {imageCount > 0 && (
                                    <span className="flex-shrink-0 text-[11px] text-[#9CA3AF]">🖼 {imageCount}</span>
                                )}
                                {videoCount > 0 && (
                                    <span className="flex-shrink-0 text-[11px] text-[#9CA3AF]">▶ {videoCount}</span>
                                )}
                            </span>


                            {/* 작성자 */}
                            <span className="text-[13px] text-[#374151] w-[72px] truncate">
                                {item.writerName}
                            </span>

                            {/* 작성일자 */}
                            <span className="text-[12px] text-[#9CA3AF] w-[64px]">
                                {dayjs(item.createdTime).format('M월 D일')}
                            </span>

                            {/* 더보기 */}
                            <span className="w-[28px] flex justify-center">
                                <span className="text-[16px] text-[#9CA3AF] leading-none tracking-widest">···</span>
                            </span>
                        </div>
                    );
                })
            ) : (
                <div className="flex items-center justify-center py-[48px] border-x border-b border-[#E2E8F0] rounded-b-[12px]">
                    <span className="text-[14px] text-[#9CA3AF]">피드가 없습니다</span>
                </div>
            )}
            <div ref={setTarget}></div>
        </Col>
    );
};

export default FeedTableView;
